import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { InitialAssessmentForm } from '../data/initial-assessment-form';
import { ILoadResult } from '../models/load-result';
import { IFormModel } from '../models/form-model';
import { ISaveResult } from '../models/save-result';
import { ApplicantDetails } from '../data/applicant-details';
import { EmployerDetails } from '../data/employer-details';
import { IAssessmentFormService } from './Iassessment-form-service';
import { environment } from '../../../environments/environment';
import { SaveFormValues } from '../data/save-form-values';
import { parseInitialAssessmentForm } from '../utils/initial-assessment-serialization.util';


export enum InitialAssessmentSettings {
    None = 0,
    isAddressForced = 1
}

export interface SettingsFlagsResult {
    initialAssessmentSettings: InitialAssessmentSettings;
}


@Injectable({
  providedIn: 'root'
})
export class AssessmentFormService implements IAssessmentFormService {
  private assessmentForm: InitialAssessmentForm | null = null;
  private username = '';
  private createdDate: string | null = null;
  
  // BehaviorSubject for reactive form state management
  private formSubject = new BehaviorSubject<InitialAssessmentForm | null>(null);
  public form$ = this.formSubject.asObservable();

  applyOrSponsor: string | null = null;
  isApplicant = true;
  saveFormValues: SaveFormValues = new SaveFormValues();

  constructor(
    private http: HttpClient,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) { }

  hasFormAndLoadedFormId(): boolean {
    return (this.assessmentForm && this.assessmentForm.loadedFormId) ? true : false;
  }

  getCreatedDate(): string | null {
    return this.createdDate ? JSON.parse(JSON.stringify(this.createdDate)) : null;
  }

  private clearData(): void {
    this.assessmentForm = null;
    this.username = '';
    this.applyOrSponsor = null;
    this.isApplicant = true;
    this.createdDate = null;
    this.saveFormValues = new SaveFormValues();
    this.formSubject.next(null);
  }

  createForm(formType: string, applyOrSponsor?: string | null): InitialAssessmentForm {
    this.assessmentForm = InitialAssessmentForm.create(formType, applyOrSponsor);
    this.formSubject.next(this.assessmentForm);
    return this.assessmentForm;
  }

  getForm(): InitialAssessmentForm | null {
    return this.assessmentForm;
  }

  getSaveFormValues(): SaveFormValues {
    return this.saveFormValues;
  }

  loadForm(username: string, pin: string): Observable<InitialAssessmentForm> {
    const queryParams = this.getQueryStringParams();
    this.clearData();

    const params = new HttpParams().set('s', queryParams['s'] || '');
    this.saveFormValues.email = username;
    this.saveFormValues.repeatEmail = username;
    this.saveFormValues.password = pin;
    
    return this.http.post<ILoadResult>(`${environment.apiUrl}/AssessmentForms/LoadForm`, {
      Username: username,
      Pin: pin
    }, { params }).pipe(
      map(response => {
        const form = parseInitialAssessmentForm(response.formDetails) as InitialAssessmentForm;
        form.loadedFormId = response.id;

        this.createdDate = response.createdDate;
        this.username = response.username;
        this.assessmentForm = InitialAssessmentForm.buildApplication(form);
        
        this.formSubject.next(this.assessmentForm);
        return this.assessmentForm;
      }),
      catchError(error => {
        console.error('Error loading form:', error);
        throw error;
      })
    );
  }

  saveForm(formModel: IFormModel): Observable<InitialAssessmentForm | null> {
    const queryParams = this.getQueryStringParams();
    this.username = formModel.username;
    this.saveFormValues.email = formModel.username;
    this.saveFormValues.repeatEmail = formModel.username;
    this.saveFormValues.password = formModel.pin;

    const params = new HttpParams().set('s', queryParams['s'] || '');

    return this.http.post<ISaveResult>(`${environment.apiUrl}/AssessmentForms/SaveForm`, formModel, { params }).pipe(
      map(response => {
        if (!this.createdDate) {
          this.createdDate = response.createdDate;
        }

        if (this.assessmentForm) {
          this.assessmentForm.loadedFormId = response.id;
        }

        if (this.assessmentForm && response.redirectUrl) {
          this.assessmentForm.redirectUrl = response.redirectUrl;
        }

        this.formSubject.next(this.assessmentForm);
        return this.assessmentForm;
      }),
      catchError(error => {
        console.error('Error saving form:', error);
        throw error;
      })
    );
  }

  updateForm(formModel: IFormModel): Observable<InitialAssessmentForm | null> {
    const queryParams = this.getQueryStringParams();
    const params = new HttpParams().set('s', queryParams['s'] || '');

    return this.http.put<ISaveResult>(`${environment.apiUrl}/AssessmentForms/UpdateForm`, formModel, { params }).pipe(
      map(response => {
        if (this.assessmentForm && response.redirectUrl) {
          this.assessmentForm.redirectUrl = response.redirectUrl;
        }

        this.formSubject.next(this.assessmentForm);
        return this.assessmentForm;
      }),
      catchError(error => {
        console.error('Error updating form:', error);
        throw error;
      })
    );
  }

  deleteForms(pin: string): Observable<boolean> {
    const queryParams = this.getQueryStringParams();
    const params = new HttpParams().set('s', queryParams['s'] || '');

    return this.http.post(`${environment.apiUrl}/AssessmentForms/deleteforms`, {
      Username: this.username,
      Pin: pin
    }, { params }).pipe(
      map(response => {
        this.clearData();
        return true;
      }),
      catchError(error => {
        console.error('Error deleting forms:', error);
        throw error;
      })
    );
  }

  getSettingsFlags(): Observable<SettingsFlagsResult> {
    return this.http.get<SettingsFlagsResult>(`${environment.apiUrl}/AssessmentForms/GetSettingsFlags`).pipe(
    map(response => {
      // Convert string enum name to number value
      let settingsValue: InitialAssessmentSettings;
      
      if (typeof response.initialAssessmentSettings === 'string') {
        // Convert string enum name to enum value
        switch (response.initialAssessmentSettings) {
          case 'None':
            settingsValue = InitialAssessmentSettings.None; // 0
            break;
          case 'isAddressForced':
            settingsValue = InitialAssessmentSettings.isAddressForced; // 1
            break;
          default:
            settingsValue = InitialAssessmentSettings.None; // Default fallback
            break;
        }
      } else {
        // If it's already a number, use it directly
        settingsValue = response.initialAssessmentSettings;
      }

      return {
        initialAssessmentSettings: settingsValue
      } as SettingsFlagsResult;
    }),
    catchError(error => {
      console.error('Error getting settings flags:', error);
      throw error;
    })
  );
  
  }


  updateFormForGeneralEnquiryType(enquiryType: string): void {
    if (this.assessmentForm) {
      if (enquiryType !== 'business') {
        this.assessmentForm.employer = null;

        if (!this.assessmentForm.applicantDetails) {
          this.assessmentForm.applicantDetails = new ApplicantDetails();
        }
        this.assessmentForm.applyOrSponsor = 'apply';
      } else {
        this.assessmentForm.applicantDetails = null;

        if (!this.assessmentForm.employer) {
          this.assessmentForm.employer = new EmployerDetails();
        }

        this.assessmentForm.applyOrSponsor = 'sponsorEmployee';
      }
      
      this.formSubject.next(this.assessmentForm);
    }
  }

  // Helper method to get query string parameters
  private getQueryStringParams(): { [key: string]: string } {
    const queryParams: { [key: string]: string } = {};
    
    this.activatedRoute.queryParams.subscribe(params => {
      Object.keys(params).forEach(key => {
        queryParams[key] = params[key];
      });
    });

    return queryParams;
  }

  // Additional Angular 18 specific methods for better state management

  // Get form as Observable for reactive programming
  getFormObservable(): Observable<InitialAssessmentForm | null> {
    return this.form$;
  }

  // Update form and notify subscribers
  updateFormState(form: InitialAssessmentForm): void {
    this.assessmentForm = form;
    this.formSubject.next(form);
  }

  // Check if form is loaded
  isFormLoaded(): boolean {
    return this.assessmentForm !== null;
  }

  // Get username
  getUsername(): string {
    return this.username;
  }

  // Set username
  setUsername(username: string): void {
    this.username = username;
  }
}